import React from 'react';
import Paper from '@material-ui/core/Paper';
import { makeStyles, Theme } from '@material-ui/core/styles';
import RadialChart from 'components/@common/Charts/RadialChart';
import Skeleton from 'components/@common/Skeleton';
import { Props as ChartProps } from 'react-apexcharts';
import { generateSeriesPercentRound } from 'utilities/radial-chart-util';
import { ApexOptions } from 'apexcharts';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      height: '100%',
      boxSizing: 'border-box',
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(3)
    },
    chart: {
      maxWidth: 300,
      margin: '0 auto'
    },
    skeleton: { margin: 'auto' }
  };
});

export type LegendFormatterFn = (seriesData: Series[]) => (seriesName: string, opts: any) => string;
export type TotalFormatterFn = (seriesData: Series[]) => { label: string; value: string };
export type Series = { count: number; total: number; label: string; color: string };
type MultiSeriesRadialChartCardProps = ChartProps & {
  seriesData: Series[];
  loading: boolean;
  legendFormatter?: LegendFormatterFn;
  totalFormatter?: TotalFormatterFn;
};
type InternalFormatterFn = (seriesData: Series[]) => string;

const defaultLengendFormatter: LegendFormatterFn = seriesData => (seriesName, opts): string => {
  const series = seriesData[opts.seriesIndex];
  return `${series.count} ${series.label}`;
};

const defaultTotalValueFormatFormatter: InternalFormatterFn = seriesData =>
  `${String(generateSeriesPercentRound(seriesData[0].count, seriesData[0].total))}%`;

const defaultTotalLabelFormatFormatter: InternalFormatterFn = seriesData => seriesData[0].label;

const MultiSeriesRadialChartCard: React.FC<MultiSeriesRadialChartCardProps> = ({
  loading,
  seriesData = [],
  legendFormatter,
  totalFormatter,
  ...props
}) => {
  const classes = useStyles();

  const config: { series: any; options: ApexOptions } = {
    series: seriesData.map(x => generateSeriesPercentRound(x.count, x.total)),
    options: {
      colors: seriesData.map(x => x.color),
      labels: seriesData.map(x => x.label),
      plotOptions: {
        radialBar: {
          offsetY: -10,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 3,
            size: '35%'
          },
          dataLabels: {
            name: {
              show: true,
              offsetY: 16,
              fontSize: '16px',
              color: '#000'
            },
            total: {
              show: true,
              fontSize: '16px',
              color: '#000',
              label:
                totalFormatter?.(seriesData)?.label ?? defaultTotalLabelFormatFormatter(seriesData),
              fontWeight: 400,
              formatter: (): string =>
                totalFormatter?.(seriesData)?.value ?? defaultTotalValueFormatFormatter(seriesData)
            },
            value: {
              show: true,
              offsetY: -16,
              fontSize: '32px',
              formatter: (val: any): string => `${String(val)}%`
            }
          }
        }
      },
      ...(legendFormatter && {
        legend: {
          show: true,
          floating: true,
          fontSize: '16px',
          position: 'left',
          offsetX: -25,
          offsetY: -2,
          labels: { useSeriesColors: true },
          // markers: { size: 0 },
          formatter: legendFormatter?.(seriesData) ?? defaultLengendFormatter(seriesData),
          itemMargin: {
            horizontal: 1,
            vertical: 1
          }
        }
      }),
      grid: {
        padding: { top: 0, right: 0, bottom: -30, left: 0 }
      }
    },
    ...props
  };
  return (
    <Paper className={classes.container}>
      {loading ? (
        <Skeleton width={250} height={250} className={classes.skeleton} />
      ) : (
        <div className={classes.chart}>
          <RadialChart height={250} series={config.series} options={config.options} />
        </div>
      )}
    </Paper>
  );
};

export default MultiSeriesRadialChartCard;
